import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { NavApi } from "@/lib/useHistoryNav";

import { acceptRequest, defaultFocus, dialogKey, promptValue } from "./dialogRules";

/**
 * The app's own confirm and prompt (日本-317).
 *
 * Replaces sixteen `window.confirm` / `window.prompt` calls — the last places the app looked like
 * a browser rather than like itself: unstyled, ignorant of `tokens.css`, uncentred on a 1147x1440
 * window, and rendered by the desktop shell as an OS-chrome box over an app that has none.
 *
 * ## Promise-based, and that is the load-bearing choice
 *
 * Every call site reads `if (window.confirm(x)) act()` or `const v = window.prompt(x)`. A promise
 * keeps all sixteen control flows exactly as they are — the diff is one line per site and a
 * reviewer can see that no behaviour moved. A render-prop or declarative-state API would rewrite
 * sixteen control flows and add sixteen pieces of state, which is where the regressions would be.
 *
 * The hazard of a promise api is one that never settles, leaving a caller's closure alive for
 * ever. That is closed structurally rather than by discipline: **`settle` is the only way the
 * request is ever cleared**, so Escape, Back, the scrim and an unmount all go through it.
 *
 * ## It mounts INSIDE `App`, and that is not a detail
 *
 * `navBridge.ts` says there is one component above `App` and is meant to stay one; this takes
 * `NavApi` as a prop instead, which is what that file asks for. It is also what makes the keyboard
 * work — see the listener below. `App` never unmounts, so nothing a rail click does can take a
 * half-answered dialog with it.
 *
 * ## The one modal-ness this deliberately does not have
 *
 * No body-scroll lock, for `Lightbox.tsx`'s reason: the app is a full-height flex layout whose
 * regions scroll internally, so there is no body scroll to lock.
 */

export type DialogTone = "default" | "danger";

export interface ConfirmRequest {
  title: string;
  /** Paragraphs, split on a blank line. Never HTML — nothing here is trusted enough for that. */
  body?: string;
  /** A mono line for the thing being counted — `283 sentences, 61 mined cards`. */
  detail?: string;
  /** Name the act, never `OK`. A button that says what it does is the last guard on a delete. */
  confirmLabel?: string;
  tone?: DialogTone;
}

export interface PromptRequest {
  title: string;
  label?: string;
  /** The syntax help that used to be crammed into the prompt's own message string. */
  hint?: string;
  initial?: string;
  placeholder?: string;
  submitLabel?: string;
  /** Reposition means position 0 when cleared; every other prompt refuses an empty field. */
  allowEmpty?: boolean;
}

export interface DialogsApi {
  confirm(request: ConfirmRequest): Promise<boolean>;
  prompt(request: PromptRequest): Promise<string | null>;
}

type OpenRequest =
  | { kind: "confirm"; request: ConfirmRequest }
  | { kind: "prompt"; request: PromptRequest };

const Context = createContext<DialogsApi | null>(null);

export function useDialogs(): DialogsApi {
  const api = useContext(Context);
  if (!api) throw new Error("useDialogs needs a DialogsProvider above it");
  return api;
}

export function DialogsProvider({
  nav,
  children,
}: {
  nav: Pick<NavApi, "openOverlay" | "closeOverlay" | "registerOverlay">;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState<OpenRequest | null>(null);
  const [draft, setDraft] = useState("");

  /** The listener below is registered once and must see live values, not a stale closure. */
  const openRef = useRef<OpenRequest | null>(null);
  const draftRef = useRef("");
  const resolveRef = useRef<((value: never) => void) | null>(null);
  const restoreFocus = useRef<Element | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  openRef.current = open;
  draftRef.current = draft;

  /**
   * Settle the promise and take the dialog down, touching history not at all.
   *
   * This is what Back calls once the entry is already gone, and it is the single point every
   * other close routes through — which is what makes "the promise always settles" a property of
   * the code rather than a thing to remember.
   */
  const settle = useCallback((value: boolean | string | null) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    openRef.current = null;
    setOpen(null);
    setDraft("");
    (restoreFocus.current as HTMLElement | null)?.focus?.();
    restoreFocus.current = null;
    resolve?.(value as never);
  }, []);

  /** Every close the user can perform: settle, then consume the entry `openOverlay` created. */
  const close = useCallback(
    (value: boolean | string | null) => {
      settle(value);
      nav.closeOverlay("dialog");
    },
    [settle, nav],
  );

  // Registered so a Back that jumps several entries can shut this without going through `close`,
  // which would try to consume an entry the browser has already left. The cancel value depends on
  // which kind was open, which is why this reads the ref rather than closing over state.
  useEffect(
    () =>
      nav.registerOverlay("dialog", () => {
        settle(openRef.current?.kind === "prompt" ? null : false);
      }),
    [nav, settle],
  );

  const request = useCallback(
    (next: OpenRequest, cancelValue: boolean | string | null) =>
      new Promise<never>((resolve) => {
        // One at a time. `openOverlay` early-returns on an id already open, so a second dialog
        // would push no entry and the first close would then consume the only one there was.
        // Unreachable by gesture — the scrim covers the app — so this guards against code.
        if (!acceptRequest(openRef.current)) {
          resolve(cancelValue as never);
          return;
        }
        resolveRef.current = resolve;
        restoreFocus.current = document.activeElement;
        openRef.current = next;
        setOpen(next);
        setDraft(next.kind === "prompt" ? (next.request.initial ?? "") : "");
        // A layer, not a location: a history entry with no URL change, so Back closes the dialog
        // and a refresh does not restore one.
        nav.openOverlay("dialog");
      }),
    [nav],
  );

  const api = useMemo<DialogsApi>(
    () => ({
      confirm: (r) => request({ kind: "confirm", request: r }, false) as Promise<boolean>,
      prompt: (r) => request({ kind: "prompt", request: r }, null) as Promise<string | null>,
    }),
    [request],
  );

  const submit = useCallback(() => {
    const current = openRef.current;
    if (!current) return;
    if (current.kind === "confirm") {
      close(true);
      return;
    }
    const { value, canSubmit } = promptValue(current.request, draftRef.current);
    if (canSubmit) close(value);
  }, [close]);

  /**
   * Keyboard precedence, which is the hard part — and the answer is the opposite of what it looks
   * like.
   *
   * Capture-phase listeners on `window` fire in **registration order, earliest first**. That is
   * precisely how `Lightbox` silences `RadicalPicker`: it registered first. So a dialog that must
   * beat both has to register **earliest**, which means at mount rather than on open:
   *
   * - `LightboxProvider` is `App`'s *parent*, and React flushes a child's effects before its
   *   parent's — so this provider, mounted inside `App`, registers before it.
   * - `RadicalPicker` registers only when the picker opens, necessarily after any mount-time one.
   * - `useShiftHeld` is a descendant and so registers first of all. Harmless: it mirrors
   *   `event.shiftKey` into a ref and reads no keys.
   *
   * Empty dependency list for `Lightbox`'s reason — re-running this effect would re-register the
   * listener at the *back* of the capture queue and hand Escape to whatever opened since.
   *
   * **The blanket `stopImmediatePropagation` is required, not defensive.** A confirm's focused
   * element is a `<button>`, so `isTextEntry` protects nothing: without it, Escape reaches
   * `Library`'s ladder and leaves the library, `a`/`e` reach Read's shortcuts, and `j`/`k` reach
   * Cards' row cursor — all from behind a scrim.
   *
   * **Its consequence has to be understood before anything is added below.** React 19 attaches
   * its listeners to the root container, which is *below* `window` in the capture phase — so while
   * a dialog is open, React's synthetic `onKeyDown` never fires anywhere, **including inside this
   * dialog's own input**. That is why Enter, Escape and Tab are decided here and why the field
   * carries no key handler. Typing still arrives (a default action, not a listener) and `onChange`
   * still fires (`input` is a different event), so the controlled field is unaffected — and so is
   * the IME, which is why `isComposing` reaches `dialogKey` rather than being assumed away.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const current = openRef.current;
      if (!current) return;
      event.stopImmediatePropagation();

      const canSubmit =
        current.kind === "confirm" ||
        promptValue(current.request, draftRef.current).canSubmit;

      switch (dialogKey(event, { kind: current.kind, canSubmit })) {
        case "cancel":
          event.preventDefault();
          close(current.kind === "prompt" ? null : false);
          break;
        case "submit":
          event.preventDefault();
          submit();
          break;
        case "focus-next":
        case "focus-prev":
          event.preventDefault();
          moveFocusWithin(panelRef.current, event.shiftKey);
          break;
        default:
          // Swallowed from the app, delivered by the browser: a letter still reaches the field and
          // Space still activates the focused button. No blanket preventDefault, so F5 still works.
          break;
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [close, submit]);

  return (
    <Context.Provider value={api}>
      {children}
      {open !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="dialog-title"
          className="fixed inset-0 flex items-center justify-center overscroll-contain p-6"
          style={{ background: "var(--overlay-scrim)", zIndex: "var(--z-modal)" }}
          onMouseDown={(event) => {
            // The scrim cancels; the card does not. Unlike the lightbox, there IS something in
            // here to interact with, so "click anywhere to close" must not be literal.
            if (event.target === event.currentTarget) {
              close(open.kind === "prompt" ? null : false);
            }
          }}
        >
          <div
            ref={panelRef}
            className="w-[min(420px,100%)] rounded-[var(--r-3)] border border-line bg-panel p-5"
            style={{ boxShadow: "var(--sh-popover)" }}
          >
            {open.kind === "confirm" ? (
              <ConfirmBody request={open.request} onAnswer={close} />
            ) : (
              <PromptBody
                request={open.request}
                value={draft}
                onChange={setDraft}
                onSubmit={submit}
                onCancel={() => close(null)}
              />
            )}
          </div>
        </div>
      )}
    </Context.Provider>
  );
}

function ConfirmBody({
  request,
  onAnswer,
}: {
  request: ConfirmRequest;
  onAnswer: (value: boolean) => void;
}) {
  const danger = request.tone === "danger";
  const focus = defaultFocus({ kind: "confirm", tone: request.tone });

  return (
    <>
      <h2 id="dialog-title" className="text-[17px]/[1.4] font-semibold text-ink">
        {request.title}
      </h2>
      {request.body && <Paragraphs text={request.body} />}
      {request.detail && (
        <p className="mt-2 font-mono text-[14px]/[1.5] text-ink-3">{request.detail}</p>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          autoFocus={focus === "cancel"}
          onClick={() => onAnswer(false)}
          data-check="dialog.cancel"
          className="h-[30px] rounded-[var(--r-2)] border border-line-strong px-[12px] text-[14px] text-ink-2 hover:bg-[color:var(--tab-hover)]"
        >
          キャンセル
        </button>
        <button
          type="button"
          autoFocus={focus === "confirm"}
          onClick={() => onAnswer(true)}
          data-check="dialog.confirm"
          className={[
            "h-[30px] rounded-[var(--r-2)] px-[12px] text-[14px] font-semibold text-white",
            danger
              ? "bg-[color:var(--accent-text)] hover:bg-[color:var(--accent-hi)]"
              : "bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)]",
          ].join(" ")}
        >
          {request.confirmLabel ?? "確認"}
        </button>
      </div>
    </>
  );
}

function PromptBody({
  request,
  value,
  onChange,
  onSubmit,
  onCancel,
}: {
  request: PromptRequest;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const { canSubmit } = promptValue(request, value);

  return (
    <>
      <h2 id="dialog-title" className="text-[17px]/[1.4] font-semibold text-ink">
        {request.title}
      </h2>
      {request.label && <p className="mt-1 text-[14px]/[1.5] text-ink-2">{request.label}</p>}
      {/* The syntax help finally has somewhere to live that is not a sentence inside a system
          alert. Mono, because every one of them is a literal you type. */}
      {request.hint && (
        <p className="mt-1 font-mono text-[14px]/[1.5] text-ink-3">{request.hint}</p>
      )}
      <input
        // No onKeyDown: while this dialog is open the window-level capture handler has already
        // stopped the event, so React's synthetic key events never fire in here. See the listener.
        autoFocus
        onFocus={(event) => event.currentTarget.select()}
        value={value}
        placeholder={request.placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="mt-3 h-[32px] w-full rounded-[var(--r-2)] border border-line-strong bg-void px-[10px] text-[15px] text-ink"
      />
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          data-check="dialog.cancel"
          className="h-[30px] rounded-[var(--r-2)] border border-line-strong px-[12px] text-[14px] text-ink-2 hover:bg-[color:var(--tab-hover)]"
        >
          キャンセル
        </button>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={onSubmit}
          data-check="dialog.confirm"
          className="h-[30px] rounded-[var(--r-2)] bg-[color:var(--accent)] px-[12px] text-[14px] font-semibold text-white hover:bg-[color:var(--accent-hover)] disabled:bg-[color:var(--ink-off)]"
        >
          {request.submitLabel ?? "確定"}
        </button>
      </div>
    </>
  );
}

/** Blank-line-separated paragraphs, so a two-sentence confirm reads as two. Never HTML. */
function Paragraphs({ text }: { text: string }) {
  return (
    <>
      {text.split(/\n{2,}/).map((paragraph, index) => (
        <p key={index} className="mt-2 text-[14px]/[1.6] text-ink-2">
          {paragraph}
        </p>
      ))}
    </>
  );
}

/**
 * Tab, by hand.
 *
 * The browser's own Tab would happily walk into the surface behind the scrim, and the capture
 * shield has already stopped anything that would otherwise do this for us. The panel holds at most
 * three focusables, so a wrap is the whole trap.
 */
function moveFocusWithin(panel: HTMLElement | null, backwards: boolean): void {
  if (!panel) return;
  const focusable = [
    ...panel.querySelectorAll<HTMLElement>("input:not([disabled]), button:not([disabled])"),
  ];
  if (focusable.length === 0) return;
  const current = focusable.indexOf(document.activeElement as HTMLElement);
  const step = backwards ? -1 : 1;
  const next = (current + step + focusable.length) % focusable.length;
  focusable[next]?.focus();
}
